import { createSelector } from '@reduxjs/toolkit';
import { chain, filter } from 'lodash';
import dayjs from 'dayjs';
import { oneBoxV3Api } from '~/api/oneBoxV3';
import {
  TOneBoxV3GetKnowledgePayload,
  TOneBoxV3GetResourcesPayload,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { RootState } from '~/store';
import {
  FileStatus,
  TDownloadListItem,
  THistoryItem,
} from '~/store/resource/resource.type';
import { fileSizeFormat, startOfDate, endOfDate } from '~/utils/helper/format';
import {
  createProgressInfo,
  createProgressLookup,
} from '~/utils/helper/downloadFile';
import { TDownloadableResource } from '~/types/common.type';
import { SearchCategory, SIDEBAR } from '~/types/enum';
import { getterFiles } from '~/store/file/fileGetter';
import { FileType } from '~/store/file/file.type';
import { set, get } from 'lodash';
import { getStoredSearchCondition } from '~/utils/helper/localStorage';

export const getterAdminResourceFilter = createSelector(
  (state: RootState) => state.resource.searchCondition,
  ({
    categories,
    versions,
    years,
    eduCodes,
    subjects,
    grades,
    fuzzySearch,
    fuzzySearchFields,
    dateFrom,
    dateTo,
    isLaunch,
    page,
    pageSize,
    sort,
  }) => ({
    categories,
    versions,
    years,
    eduCodes,
    subjects,
    grades,
    fuzzySearch,
    fuzzySearchFields,
    dateFrom,
    dateTo,
    isLaunch,
    pagination: {
      pageSize,
      page,
      sort,
    },
  }),
);

export const getterClientResourceFilter = createSelector(
  (state: RootState) => state.resource.searchCondition,
  ({ eduCodes, years, grades, subjects, resources1 }) => {
    const resourceSearchKey = getStoredSearchCondition();
    const combinedSearchKey = {
      ...resourceSearchKey,
      ...(eduCodes !== null && { eduCodes }),
      ...(years !== null && { years }),
      ...(grades !== null && { grades }),
      ...(subjects !== null && { subjects }),
      ...(resources1 !== null && { resources1 }),
    };
    return combinedSearchKey;
  },
);

export const getterResources = createSelector(
  [
    (state: RootState) => state.resource.resources,
    (state, params: TOneBoxV3GetResourcesPayload) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      oneBoxV3Api.endpoints.getResources.select(params)(state)?.data,
  ],
  (initialData, resourceData) => {
    if (
      resourceData &&
      resourceData.data &&
      resourceData.data.data.length > 0
    ) {
      return {
        resources: resourceData.data.data,
        totalResourcesCount: resourceData.data.totalAmount,
        totalPages: resourceData.data.totalPages,
      };
    }
    return {
      resources: initialData,
      totalResourcesCount: 0,
      totalPages: 0,
    };
  },
);

export const getterAdminDownloadList = createSelector(
  getterResources,
  ({ resources }) =>
    chain(resources)
      .map(
        ({
          categories,
          versions,
          years,
          eduCodes,
          subjects,
          fileName,
          file,
          grades,
          originalFileId,
          resources1,
          resources2,
          resources3,
          displayName,
          chapterKeyword,
        }) => {
          return {
            originalFileId: originalFileId,
            createdAt: file.fileUploadTime,
            categories,
            versions,
            years,
            eduCodes,
            subjects,
            grades,
            resources1,
            resources2,
            resources3,
            displayName,
            fileName,
            chapterKeyword,
          };
        },
      )
      .value(),
);

export const getterAdminResources = createSelector(
  getterResources,
  ({ resources, totalResourcesCount }) => {
    const newResources = chain(resources)
      .map(
        ({
          _id,
          years,
          isLaunch,
          displayName,
          fileName,
          file,
          categories,
          eduCodes,
          grades,
          resources1,
          resources2,
          resources3,
          subjects,
          versions,
          chapterKeyword,
          knowledge,
          originalFileId,
        }) => {
          return {
            _id,
            originalFileId: originalFileId,
            createdAt: file.fileUploadTime,
            isLaunch: !!isLaunch,
            url: file.url,
            fileName: fileName || '-',
            displayName: displayName || '-',
            categories: {
              origin: file.category,
              additional: filter(categories, item => item !== file.category),
            },
            eduCodes: {
              origin: file.eduCode,
              additional: chain(eduCodes)
                .filter(item => item !== file.eduCode)
                .value(),
            },
            grades: {
              origin: file.grade,
              additional: filter(grades, item => item !== file.grade),
            },
            resources1: {
              origin: file.resource1,
              additional: filter(
                resources1 || [],
                item => item !== file.resource1,
              ),
            },
            resources2: {
              origin: file.resource2,
              additional: filter(
                resources2 || [],
                item => item !== file.resource2,
              ),
            },
            resources3: {
              origin: file.resource3,
              additional: filter(
                resources3 || [],
                item => item !== file.resource3,
              ),
            },
            subjects: {
              origin: file.subject,
              additional: filter(subjects, item => item !== file.subject),
            },
            versions: {
              origin: file.version,
              additional: filter(versions, item => item !== file.version),
            },
            years: {
              origin: file.year,
              additional: filter(years, item => item !== file.year),
            },
            chapterKeyword: {
              origin: undefined,
              additional: chapterKeyword,
            },
            knowledge: {
              origin: undefined,
              additional: knowledge,
            },
          };
        },
      )
      .value();

    return {
      resources: newResources,
      totalResourcesCount,
    };
  },
);

export const getterAdminResourcesPayload = createSelector(
  (state: RootState) => state.resource.searchCondition,
  ({
    eduCodes,
    years,
    grades,
    subjects,
    categories,
    versions,
    fuzzySearch,
    fuzzySearchFields,
    isLaunch,
    dateFrom,
    dateTo,
    page,
    pageSize,
    sort,
  }) => ({
    search: {
      ...(eduCodes !== 'all' && eduCodes && { eduCodes }),
      ...(years !== 'all' && years && { years }),
      ...(grades !== 'all' && grades && { grades }),
      ...(subjects !== 'all' && subjects && { subjects }),
      ...(categories && {
        categories:
          categories === SearchCategory.INTERSECTION
            ? [SearchCategory.CRAM_SCHOOL, SearchCategory.SCHOOL]
            : [categories],
      }),
      ...(versions !== 'all' && versions && { versions }),
    },
    ...(fuzzySearch && { fuzzySearch }),
    ...(fuzzySearchFields && fuzzySearchFields[0] && { fuzzySearchFields }),
    isLaunch,
    ...(dateFrom && { dateFrom: startOfDate(dayjs(dateFrom)) }),
    ...(dateTo && { dateTo: endOfDate(dayjs(dateTo)) }),
    pagination: {
      page,
      pageSize,
      sort,
    },
  }),
);

export const getterClientResourcesPayload = createSelector(
  (state: RootState) => state.resource.searchCondition,
  ({
    eduCodes,
    years,
    grades,
    subjects,
    // category,
    // version,
    // fuzzySearch,
    // isLaunch,
    // dateFrom,
    // dateTo,
    resources1,
    page,
    pageSize,
  }) => ({
    search: {
      ...(eduCodes !== 'all' && eduCodes && { eduCodes }),
      ...(years !== 'all' && years && { years }),
      ...(grades !== 'all' && grades && { grades }),
      ...(subjects !== 'all' && subjects && { subjects }),
      ...(resources1 && { resources1 }),
      // ...(category !== 'all' && category && { category }),
      // ...(version !== 'all' && version && { version }),
    },
    isLaunch: true,
    pagination: {
      // API 分頁為 1-indexed（page=0 會被當成第 1 頁），store 內部維持 0-indexed，
      // 送出 payload 時 +1，否則第 1 頁會重複抓、最後一頁永遠抓不到
      page: page + 1,
      pageSize,
      sort: 'displayName',
      paginateBy:
        resources1 &&
        (resources1 as SIDEBAR) !== SIDEBAR.ebook &&
        (resources1 as SIDEBAR) !== SIDEBAR.tiku
          ? 'resources2'
          : null,
    },
  }),
);

export const getterProgressList = createSelector(
  (state: RootState) => state.resource.progressList,
  progressList => {
    return Array.from(progressList).map(item => item[1]);
  },
);

export const getterDownloadList = createSelector(
  (state: RootState) => state.resource.downloadList,
  downloadList => {
    const list: TDownloadListItem[] = [];
    Object.entries(downloadList || {}).forEach(arr => {
      /**
       * @description 下載路徑但沒有 .檔案類型
       */
      const basePath = arr[0];
      Object.entries(arr[1]).forEach(subArr => {
        const fileFormat = subArr[0];
        const detail = subArr[1];
        list.push({
          path: basePath,
          fileFormat,
          ...detail,
        });
      });
    });
    return list;
  },
);

/**
 * 依檔案 url 查即時下載狀態。資源頁的卡片與單檔下載列表要用這個，不要從
 * getterClientResources 找：它綁著目前頁碼的查詢結果，前面頁面的資源查不到（NBX3-1117）。
 */
export const getterProgressLookup = createSelector(
  [getterProgressList, getterDownloadList],
  (progressList, downloadList) =>
    createProgressLookup(progressList, downloadList),
);

export const getterClientResources = createSelector(
  [getterResources, getterProgressList, getterDownloadList, getterFiles],
  (
    { resources, totalResourcesCount, totalPages },
    progressList,
    downloadList,
    files,
  ) => {
    return {
      resources: resources.map(resource => {
        const progressItem = progressList.find(
          item => item.url === resource.file.url,
        );
        const downloadItem = downloadList.find(
          item => item.url === resource.file.url,
        );
        const fileItem = files.find(({ fileName, type, subFolder }) => {
          if (type === FileType.flash && subFolder === '南一電子教科書') {
            return (
              fileName ===
              resource.fileName
                .split('.')[0]
                .replace('電子書', '')
                .replace('南一', '')
            );
          } else {
            return fileName === resource.fileName;
          }
        });
        return {
          resource,
          progress: createProgressInfo({ progressItem, downloadItem }),
          file: fileItem,
        };
      }),
      totalResourcesCount,
      totalPages,
    };
  },
);

/**
 * @description 暫定為三層結構資源
 */
export const getterMultiLevelClientResources = createSelector(
  [getterResources, getterProgressList, getterDownloadList],
  ({ resources }, progressList, downloadList) => {
    const target: {
      [resource1: string]: {
        [resource2: string]: {
          [resource3: string]: TDownloadableResource[];
        };
      };
    } = {};
    resources.forEach(resource => {
      const resources1 = resource.resources1;
      const resources2 =
        resource.resources2.length > 0 ? resource.resources2 : resources1; // 如果沒有第二層資源，則第二層資源為第一層資源
      const resources3 =
        resource.resources3.length > 0 ? resource.resources3 : resources2; // 如果沒有第三層資源，則第三層資源為第二層資源

      const progressItem = progressList.find(
        item => item.url === resource.file.url,
      );
      const downloadItem = downloadList.find(
        item => item.url === resource.file.url,
      );

      const info = {
        resource,
        progress: createProgressInfo({ downloadItem, progressItem }),
      };

      resources1.forEach(v => {
        resources2.forEach(v2 => {
          resources3.forEach(v3 => {
            set(target, [v, v2, v3], [...get(target, [v, v2, v3], []), info]);
          });
        });
      });
      // const resource1 = resource.file.resource1 || '';
      // const resource2 = resource.file.resource2 || resource1;
      // const resource3 = resource.file.resource3 || resource2;
      // const progressItem = progressList.find(
      //   item => item.url === resource.file.url,
      // );
      // const downloadItem = downloadList.find(
      //   item => item.url === resource.file.url,
      // );

      // const info = {
      //   resource,
      //   progress: createProgressInfo({ downloadItem, progressItem }),
      // };

      // set(
      //   target,
      //   [resource1, resource2, resource3],
      //   [...get(target, [resource1, resource2, resource3], []), info],
      // );
    });
    return target;
  },
);

export const getterHistory = createSelector(
  [getterProgressList, getterDownloadList],
  (progressList, downloadList) => {
    const history: {
      [
        status in
          FileStatus.progressing | FileStatus.finish | FileStatus.installing
      ]: THistoryItem[];
    } = {
      [FileStatus.progressing]: [],
      [FileStatus.finish]: [],
      [FileStatus.installing]: [],
    };

    downloadList
      .sort((a, b) => b.downloadedAt - a.downloadedAt)
      .forEach(
        ({
          url,
          filename,
          fileFormat,
          status,
          fileSize,
          path,
          installingProgress,
          downloadedAt,
          updateTime,
        }) => {
          if (status === FileStatus.cancel) {
            return;
          }

          const basicInfo = {
            key: `${path}.${fileFormat}`,
            url,
            fileName: filename,
            fileFormat,
            status,
            downloadedAt,
            updateTime,
            totalBytes: fileSize,
          };

          const existedProgressItem = progressList.find(
            subItem => subItem.url === url,
          );

          if (
            existedProgressItem &&
            (status === FileStatus.progressing || status === FileStatus.paused)
          ) {
            const { progress, total } = existedProgressItem;
            history[FileStatus.progressing].push({
              ...basicInfo,
              progress: Math.floor(progress),
              size: total,
            });
            return;
          }

          if (status === FileStatus.installing) {
            const sizeObj = fileSizeFormat(fileSize);
            history[FileStatus.installing].push({
              ...basicInfo,
              progress: installingProgress,
              size: sizeObj.size + sizeObj.unit,
            });
            return;
          }

          if (status === FileStatus.completed || status === FileStatus.finish) {
            const sizeObj = fileSizeFormat(fileSize);
            history[FileStatus.finish].push({
              ...basicInfo,
              progress: 100,
              size: sizeObj.size + sizeObj.unit,
            });
            return;
          }
        },
      );
    return history;
  },
);

export const getterHasTotalDownloading = createSelector(
  [
    (state: RootState) => state.resource.totalDownloadingList,
    (state: RootState) => state.resource.searchCondition.resources1,
  ],
  (totalDownloadingList, sidebars) => (labelSeries: string[]) =>
    !!totalDownloadingList.find(
      ({ label, subLabel, lastLabel }) =>
        label === (sidebars as string) &&
        subLabel === labelSeries[0] &&
        lastLabel === labelSeries[1],
    ),
);

export const getterKnowledgeChaptersList = createSelector(
  [
    (state: RootState) => state.resource.knowledgeChaptersList,
    (state, params: TOneBoxV3GetKnowledgePayload) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      oneBoxV3Api.endpoints.getKnowledge.select(params)(state)?.data,
  ],
  (initialData, knowledgeChaptersData) => {
    const data = knowledgeChaptersData?.data;
    if (data && Array.isArray(data.chapters)) {
      return {
        bookId: data.bookId,
        chapters: data.chapters,
      };
    }
    return {
      bookId: '',
      chapters: initialData,
    };
  },
);

export const getterChaptersToKnowledge = createSelector(
  getterKnowledgeChaptersList,
  ({ chapters }) =>
    (Array.isArray(chapters) ? chapters : []).reduce(
      (acc, chapter) => {
        const { code, knowledgeIndexes } = chapter;
        acc[code] = knowledgeIndexes;
        return acc;
      },
      {} as { [key: string]: string[] },
    ),
);
